// @flow
import { pick } from "lodash";
import { startsWith } from "voca";
import { getLogger } from "@cld/upload-widget";
import prefetchImages from "services/imagesPrefetcher";
import {
	selectCustomImagesResults,
	selectCustomImagesToLoad,
} from "../selectors";

const logger = getLogger();

const ALLOWED_IMAGE_PATHS = { // todo: add all allowed paths here or overrides wont be used
	cameraIcon: "sources",
	cameraIconActive: "sources",
	dropboxIcon: "sources",
	dropboxIconActive: "sources",
	facebookIcon: "source",
	facebookActiveIcon: "source",
	logoutIcon: "sourceSection",
};
const ALLOWED_IMAGE_KEYS = Object.keys(ALLOWED_IMAGE_PATHS);

const reportAllImagesAsFailed = (images: Object, dispatcher: Function, ats: Object) => {
	const results = Object.keys(images).reduce((res, key) => {
		res[key] = { success: false, key, reason: "unexpected" };
		return res;
	}, {});

	dispatcher(ats.SET_CUSTOM_IMAGES_LOAD_RESULTS, { results });
};

const loadImages = async (images: Object, dispatcher: Function, ats: Object) => {
	try {
		const results = await prefetchImages(images);

		logger.log("[root.configImagesLoader]: image loader worker results: ", results);
		dispatcher(ats.SET_CUSTOM_IMAGES_LOAD_RESULTS, { results });
	} catch (ex) {
		logger.error("[configImageLoader]: unexpected error when trying to load custom images", ex);
		reportAllImagesAsFailed(images, dispatcher, ats);
	}
};

const getPathFromObject = (obj: Object, path: string) => {
	const parts = path.split(".");
	let result = obj;

	for (let i = 0; i < parts.length; i++) {
		result = result[parts[i]] ? result[parts[i]] : null;

		if (!result) {
			break;
		}
	}

	return result;
};

const getAllowedImageUrlsToLoad = (elements: Object) => {
	const cache = {};

	return ALLOWED_IMAGE_KEYS.reduce((res, key) => { // only get images declared in the const above
		const keyPath = ALLOWED_IMAGE_PATHS[key];
		const pathParts = keyPath.split(".");

		if (!cache[keyPath]) {
			for (let i = 0; i < pathParts.length; i++) { // fill the cache with the possible parts
				const subPath = pathParts.slice(0, i + 1).join(".");

				if (!cache[subPath]) {
					cache[subPath] = getPathFromObject(elements, subPath);
				}
			}
		}

		if (cache[keyPath] && cache[keyPath][key] && startsWith(cache[keyPath][key], "http")) {
			res[key] = cache[keyPath][key];
		}

		return res;
	}, {});
};

const filterToNewImages = (images: Object, getState: Function) => {
	const state = getState();
	const toLoad = selectCustomImagesToLoad(state);
	const loadResults = selectCustomImagesResults(state);

	return Object.keys(images).reduce((res, key) => {
		if (!loadResults[key] && !toLoad[key]) { // dont store an image that was already loaded or requested
			res[key] = images[key];
		}

		return res;
	}, {});
};

export default async (config: Object, dispatcher: Function, ats: Object, getState: Function) => {
	if (config.styles) {
		let images = {};

		if (config.styles.elements) {
			images = getAllowedImageUrlsToLoad(config.styles.elements);
		}

		if (config.styles.images) {
			images = {
				...images,
				...pick(config.styles.images, ALLOWED_IMAGE_KEYS),
			};
		}

		if (Object.keys(images).length) {
			images = filterToNewImages(images, getState);

			if (Object.keys(images).length) {
				logger.log("[configImagesLoader]: about to prefetch custom images: ", images);
				dispatcher(ats.SET_CUSTOM_IMAGES_TO_LOAD, { images }); // store keys about to be loaded

				await loadImages(images, dispatcher, ats);
			}
		}
	}
};
